import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHero } from "@/components/Sections";
import { Skeleton } from "@/components/ui/skeleton";
import { Telescope, X } from "lucide-react";

const telescopes = [
  { id: "james-webb", label: "James Webb" },
  { id: "hubble", label: "Hubble" },
  { id: "chandra", label: "Chandra X-ray" },
  { id: "spitzer", label: "Spitzer Infrared" },
  { id: "kepler", label: "Kepler" },
  { id: "galaxies", label: "Galaxies" },
  { id: "nebulae", label: "Nebulae" },
  { id: "black-holes", label: "Black Holes" },
];

export default function SpaceGallery() {
  const [tab, setTab] = useState(telescopes[0]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/telescope/${tab.id}`);
        if (!cancelled) setItems(r.data.items || []);
      } catch (e) {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tab]);

  const spotlight = items[0];
  const grid = items.slice(1, 20);

  return (
    <>
      <PageHero
        eyebrow="Space Gallery News"
        title="The latest light"
        accent="from the cosmos."
        subtitle="Actual images of the universe, captured by the world's most powerful telescopes — with the most recent capture in spotlight and the latest 20 below, each with its own explanation."
      >
        <div className="flex flex-wrap gap-2">
          {telescopes.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t)}
              data-testid={`gallery-tab-${t.id}`}
              className={`btn-pill text-[12px] ${tab.id === t.id ? "btn-primary" : "btn-secondary"}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </PageHero>

      <section className="max-w-7xl mx-auto px-6 pb-12">
        {loading ? (
          <Skeleton className="h-[55vh] w-full rounded-3xl" />
        ) : spotlight ? (
          <button
            onClick={() => setSelected(spotlight)}
            data-testid="gallery-spotlight"
            className="glass-strong p-5 md:p-8 w-full text-left hover-lift fade-in-up"
          >
            <div className="grid lg:grid-cols-12 gap-8 items-start">
              <div className="lg:col-span-7">
                <div className="aspect-[16/10] overflow-hidden rounded-2xl bg-white/5">
                  <img src={spotlight.img} alt={spotlight.title} className="w-full h-full object-cover" loading="lazy" />
                </div>
              </div>
              <div className="lg:col-span-5">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/8 border border-white/15 text-[11px] uppercase tracking-widest text-white/80">
                  <Telescope size={12} /> Latest · {tab.label}
                </div>
                <h2 className="display mt-5 text-2xl md:text-3xl lg:text-4xl tracking-tight">{spotlight.title}</h2>
                {spotlight.date_created && (
                  <div className="mt-4 text-sm text-white/55">{new Date(spotlight.date_created).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</div>
                )}
                {spotlight.description && (
                  <p className="mt-4 text-[14px] text-white/70 leading-relaxed line-clamp-6">{spotlight.description}</p>
                )}
                <div className="mt-6 text-sm text-white/65">Tap to read more</div>
              </div>
            </div>
          </button>
        ) : (
          <div className="glass p-10 text-white/70" data-testid="gallery-empty">
            No imagery available right now. Please check back in a little while.
          </div>
        )}
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24">
        {!loading && grid.length > 0 && (
          <>
            <div className="eyebrow-row mb-6"><span className="eyebrow">More from {tab.label}</span></div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 stagger">
              {grid.map((it) => (
                <button
                  key={it.nasa_id}
                  onClick={() => setSelected(it)}
                  data-testid={`gallery-item-${it.nasa_id}`}
                  className="glass hover-lift overflow-hidden text-left group flex flex-col"
                >
                  <div className="aspect-[4/3] overflow-hidden bg-white/5">
                    <img src={it.thumb} alt={it.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <div className="text-[11px] text-white/45">{(it.date_created || "").slice(0, 10)}</div>
                    <div className="mt-2 text-[14px] font-semibold tracking-tight line-clamp-2">{it.title}</div>
                    {it.description && (
                      <div className="mt-2 text-xs text-white/55 leading-relaxed line-clamp-3">{it.description}</div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </section>

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-xl p-4 md:p-8 overflow-y-auto" onClick={() => setSelected(null)} data-testid="gallery-modal">
          <div className="max-w-5xl mx-auto glass p-5 md:p-8 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setSelected(null)} className="absolute top-4 right-4 w-9 h-9 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 flex items-center justify-center" aria-label="Close">
              <X size={16} />
            </button>
            <div className="grid md:grid-cols-12 gap-6">
              <div className="md:col-span-7">
                <img src={selected.img} alt={selected.title} className="w-full rounded-2xl" />
              </div>
              <div className="md:col-span-5">
                <div className="eyebrow">{(selected.date_created || "").slice(0, 10)} · {tab.label}</div>
                <h3 className="display mt-3 text-2xl md:text-3xl">{selected.title}</h3>
                {selected.description && (
                  <p className="mt-4 text-sm text-white/70 leading-relaxed max-h-80 overflow-y-auto pr-2">{selected.description}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
